import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const rand = () => `t_${Math.random().toString(36).slice(2, 8)}`;

export const ProjectTemplates = ({ canManage }) => {
  const [templates, setTemplates] = useState([]);
  const [types, setTypes] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/project-templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/project-product-types").then((r) => setTypes(r.data.product_types || [])).catch(() => {});
  }, []);

  const blank = () => ({ product_type: types[0] || "", name: "", description: "", target_days: 15, steps: [] });

  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/project-templates/${t.template_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div>
      {canManage && <div className="mb-3 flex justify-end"><Button data-testid="new-template-button" size="sm" onClick={() => setEditing(blank())} className="bg-primary hover:bg-primary/90 text-primary-foreground"><Plus className="h-4 w-4" /> New template</Button></div>}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {templates.map((t) => (
          <Card key={t.template_id} className="border-slate-200 p-5" data-testid={`template-card-${t.template_id}`}>
            <div className="flex items-start justify-between">
              <div>
                <span className="rounded bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">{t.product_type}</span>
                <h3 className="mt-2 font-heading text-base font-semibold text-slate-900">{t.name}</h3>
                <p className="mt-0.5 text-sm text-slate-500">{t.steps.length} steps · {t.steps.reduce((a, s) => a + s.tasks.length, 0)} tasks · target {t.target_days}d</p>
              </div>
              {canManage && (
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-template-${t.template_id}`} onClick={() => setEditing(JSON.parse(JSON.stringify(t)))}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => del(t)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
      {editing && <TemplateEditor tpl={editing} types={types} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};

const TemplateEditor = ({ tpl, types, onClose, onSaved }) => {
  const [form, setForm] = useState({ ...tpl, steps: (tpl.steps || []).map((s) => ({ ...s, tasks: s.tasks.map((t) => ({ ...t, key: t.key || rand() })) })) });

  const allTasks = form.steps.flatMap((s) => s.tasks);

  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, { name: `Step ${f.steps.length + 1}`, order: f.steps.length, tasks: [] }] }));
  const updStep = (i, patch) => setForm((f) => ({ ...f, steps: f.steps.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const delStep = (i) => setForm((f) => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));
  const addTask = (si) => updStep(si, { tasks: [...form.steps[si].tasks, { key: rand(), name: "", owner_side: "provider", visibility: "internal", est_days: 1, depends_on: [] }] });
  const updTask = (si, ti, patch) => updStep(si, { tasks: form.steps[si].tasks.map((t, idx) => idx === ti ? { ...t, ...patch } : t) });
  const delTask = (si, ti) => updStep(si, { tasks: form.steps[si].tasks.filter((_, idx) => idx !== ti) });

  const save = async () => {
    if (!form.name || !form.product_type) { toast.error("Name and product type are required"); return; }
    const payload = { ...form, target_days: Number(form.target_days) || 15 };
    try {
      if (form.template_id) await api.put(`/project-templates/${form.template_id}`, payload);
      else await api.post("/project-templates", payload);
      toast.success("Template saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader><DialogTitle>{form.template_id ? "Edit template" : "New template"}</DialogTitle><DialogDescription>Define the steps, tasks and dependencies for this product type.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Product type</Label>
              <Select value={form.product_type} onValueChange={(v) => setForm({ ...form, product_type: v })}>
                <SelectTrigger data-testid="tpl-product-type"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-1 space-y-1.5"><Label>Target days</Label><Input type="number" data-testid="tpl-target-days" value={form.target_days} onChange={(e) => setForm({ ...form, target_days: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Template name</Label><Input data-testid="tpl-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          </div>

          {form.steps.map((step, si) => (
            <Card key={si} className="border-slate-200 p-3" data-testid={`tpl-step-${si}`}>
              <div className="flex items-center gap-2">
                <Input value={step.name} onChange={(e) => updStep(si, { name: e.target.value })} className="h-8 font-medium" placeholder="Step name" />
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => delStep(si)}><X className="h-4 w-4 text-red-500" /></Button>
              </div>
              <div className="mt-2 space-y-2">
                {step.tasks.map((t, ti) => (
                  <div key={t.key} className="rounded-lg border border-slate-100 bg-slate-50 p-2.5" data-testid={`tpl-task-${si}-${ti}`}>
                    <div className="flex items-center gap-2">
                      <Input value={t.name} onChange={(e) => updTask(si, ti, { name: e.target.value })} className="h-8 flex-1" placeholder="Task name" />
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => delTask(si, ti)}><X className="h-3.5 w-3.5 text-red-500" /></Button>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <Select value={t.owner_side} onValueChange={(v) => updTask(si, ti, { owner_side: v })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="provider">Provider</SelectItem><SelectItem value="partner">Partner</SelectItem></SelectContent>
                      </Select>
                      <Select value={t.visibility} onValueChange={(v) => updTask(si, ti, { visibility: v })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="internal">Internal</SelectItem><SelectItem value="shared">Shared (customer)</SelectItem></SelectContent>
                      </Select>
                      <Input type="number" value={t.est_days} onChange={(e) => updTask(si, ti, { est_days: Number(e.target.value) })} className="h-8 text-xs" placeholder="Est days" />
                    </div>
                    {allTasks.filter((x) => x.key !== t.key && x.name).length > 0 && (
                      <div className="mt-2">
                        <div className="text-[11px] font-medium text-slate-400">Depends on:</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {allTasks.filter((x) => x.key !== t.key && x.name).map((x) => {
                            const on = (t.depends_on || []).includes(x.key);
                            return (
                              <button key={x.key} type="button" onClick={() => updTask(si, ti, { depends_on: on ? t.depends_on.filter((k) => k !== x.key) : [...(t.depends_on || []), x.key] })}
                                className={`rounded-full border px-2 py-0.5 text-[11px] ${on ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-500 hover:bg-slate-100"}`}>
                                {x.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" data-testid={`add-task-${si}`} onClick={() => addTask(si)}><Plus className="h-3.5 w-3.5" /> Add task</Button>
              </div>
            </Card>
          ))}
          <Button variant="outline" size="sm" data-testid="add-step" onClick={addStep}><Plus className="h-4 w-4" /> Add step</Button>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="tpl-save" onClick={save} className="bg-primary hover:bg-primary/90 text-primary-foreground">Save template</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
